function _EMOTION_STRINGIFIED_CSS_ERROR__() { return "You have tried to stringify object returned from `css` function. It isn't supposed to be used directly (e.g. as value of the `className` prop), but rather handed to emotion so it can handle it (e.g. as value of `css` prop)."; }
import { css } from '@emotion/react';
import { superstructResolver } from '@hookform/resolvers/superstruct';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import { useEffect, useReducer } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { assign, coerce, object, optional, pick, string } from 'superstruct';
import { ButtonCancel, ButtonSave } from '@arc/frontend/ui-buttons';
import { InputTextFieldWithControl, PasswordInput } from '@arc/frontend/ui-form';
import { Modal } from '@arc/frontend/ui-modal';
import { typography } from '@arc/frontend/ui-theme';
import { createSuccessToast } from '@arc/frontend/ui-toasts';
import { useAuth, useRefreshUser } from '@arc/frontend/util-auth';
import { apiRequest, handleRequestErrors } from '@arc/frontend/util-query';
import { matchesField, password } from '@arc/shared/util-superstruct';
import { AddUserDto, DUPLICATE_USER_EMAIL, EditUserProfile } from '@arc/shared/util-users';
const UserProfileSchema = assign(pick(AddUserDto, ['name', 'email']), object({
  verifiedPassword: optional(matchesField('password', 'user-form:Passwords do not match.')),
  password: optional(coerce(password(), string(), value => value === '' ? undefined : value))
}));
async function editProfile(dto) {
  return await apiRequest(EditUserProfile, {
    body: dto
  });
}
const styles = {
  row: process.env.NODE_ENV === "production" ? {
    name: "ywfvzt",
    styles: "display:grid;width:100%;grid-template-columns:1fr 1fr;gap:1rem"
  } : {
    name: "18gcg9s-row",
    styles: "display:grid;width:100%;grid-template-columns:1fr 1fr;gap:1rem;label:row;",
    toString: _EMOTION_STRINGIFIED_CSS_ERROR__
  }
};
var _ref = process.env.NODE_ENV === "production" ? {
  name: "1mys3sh",
  styles: "margin-left:1rem"
} : {
  name: "1shn344-UserProfileModal",
  styles: "margin-left:1rem;label:UserProfileModal;",
  toString: _EMOTION_STRINGIFIED_CSS_ERROR__
};
export function UserProfileModal({
  show,
  onCancel,
  onSave
}) {
  const {
    t
  } = useTranslation('user-form');
  const {
    user
  } = useAuth();
  const refreshUser = useRefreshUser();
  const {
    mutateAsync: editUserRequest
  } = useMutation({
    mutationFn: editProfile
  });
  const queryClient = useQueryClient();
  const formMethods = useForm({
    resolver: superstructResolver(UserProfileSchema, {
      coerce: true,
      mask: true
    })
  });
  const {
    reset,
    setValue
  } = formMethods;
  const [changePassword, toggleChangePassword] = useReducer(state => !state, false);
  const handleCancelChangePassword = () => {
    setValue('password', undefined);
    setValue('verifiedPassword', undefined);
  };
  const handleCancel = () => {
    if (changePassword) toggleChangePassword();
    onCancel();
  };
  const handleSave = async data => {
    try {
      const res = await editUserRequest(data);
      if ('message' in res) {
        if (res.message === DUPLICATE_USER_EMAIL) {
          formMethods.setError('email', {
            message: DUPLICATE_USER_EMAIL
          });
        }
        return;
      }
      const updatedUser = res;
      void queryClient.invalidateQueries({
        queryKey: ['user']
      });
      void queryClient.invalidateQueries({
        queryKey: ['users']
      });
      createSuccessToast(t('submit toast success', {
        user: updatedUser
      }));
      if (changePassword) toggleChangePassword();
      void refreshUser();
      onSave();
    } catch (err) {
      handleRequestErrors([err], {
        errorMessage: t('submit toast error')
      });
    }
  };

  // Change default values when user changes (either from different login or after user update)
  // or when hiding and reshowing modal
  useEffect(() => {
    const values = _.pick({
      ...user,
      password: undefined,
      verifiedPassword: undefined,
      phone: undefined
    }, Object.keys(UserProfileSchema.schema));
    reset(values);
  }, [user, reset, show]);
  return <Modal show={show} dialogStyleOverrides={{
    width: '560px'
  }}>
			<Modal.Header containerStyle={{
      display: 'flex',
      justifyContent: 'space-between'
    }}>
				{t('profile header')}
			</Modal.Header>
			<Modal.Body>
				<FormProvider {...formMethods}>
					<div css={styles.row}>
						<InputTextFieldWithControl name="name" label={t('Name')} placeholder={t('Name')} data-testid="name" autoCapitalize="words" outerStyle={{
            width: '100%'
          }} />
						<InputTextFieldWithControl name="email" label={t('Email')} placeholder={t('Email')} data-testid="email" />
					</div>
					<button type="button" onClick={() => {
          if (changePassword) handleCancelChangePassword();
          toggleChangePassword();
        }} css={[typography.clickableLink, "margin-bottom:0.75rem;" + (process.env.NODE_ENV === "production" ? "" : ";label:UserProfileModal;")]} data-testid="change-password">
						{t('Change password')}
					</button>
					{changePassword ? <div css={styles.row}>
							<PasswordInput name="password" label={t('New password')} placeholder={t('New password')} data-testid="new-password" disablePasswordManager={false} />

							<PasswordInput name="verifiedPassword" label={t('Verify New Password')} placeholder={t('Verify New Password')} data-testid="verify-password" disablePasswordManager={false} />
						</div> : null}
				</FormProvider>
			</Modal.Body>
			<Modal.Footer containerStyle={{
      display: 'flex',
      justifyContent: 'space-between',
      paddingTop: '0px'
    }}>
				<div />
				<div>
					<ButtonCancel onClick={handleCancel} />
					<ButtonSave css={_ref} onClick={formMethods.handleSubmit(handleSave)} />
				</div>
			</Modal.Footer>
		</Modal>;
}